import { useQuery, useQueryClient } from '@tanstack/react-query';

import { HttpError } from '@revelio/data-access';

import { useEffectiveUserId } from '../../adminRewrite/impersonation/useEffectiveUserId';
import {
  createReportDocument as apiCreateReportDocument,
  deleteReportDocument as apiDeleteReportDocument,
  updateReportDocument as apiUpdateReportDocument,
  getReportDocument,
  getReportDocuments,
} from '../api/report-documents-api';
import { useOptimisticListMutations } from '../hooks/use-optimistic-list-mutations';
import type { ReportDocumentConfig } from './report-document-types';

const reportDocumentQueryKeys = {
  all: ['report-documents'] as const,
  lists: (userId?: string) =>
    [...reportDocumentQueryKeys.all, 'list', userId ?? 'self'] as const,
  details: () => [...reportDocumentQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...reportDocumentQueryKeys.details(), id] as const,
} as const;

export function useReportDocumentsQuery({
  enabled = true,
}: {
  enabled?: boolean;
} = {}) {
  const userId = useEffectiveUserId();
  return useQuery<ReportDocumentConfig[]>({
    queryKey: reportDocumentQueryKeys.lists(userId),
    queryFn: () => getReportDocuments(userId),
    enabled,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

export function useFetchReportDocument(
  documentId: string,
  { enabled = true }: { enabled?: boolean } = {}
) {
  return useQuery<ReportDocumentConfig | null>({
    queryKey: reportDocumentQueryKeys.detail(documentId),
    queryFn: async () => {
      try {
        return await getReportDocument(documentId);
      } catch (error) {
        if (error instanceof HttpError && error.status === 404) {
          return null;
        }
        throw error;
      }
    },
    enabled,
    retry: 1,
  });
}

export function useReportDocumentMutations() {
  const queryClient = useQueryClient();
  const userId = useEffectiveUserId();

  const { create, update, remove } = useOptimisticListMutations<
    ReportDocumentConfig,
    ReportDocumentConfig,
    ReportDocumentConfig,
    void
  >({
    listKey: reportDocumentQueryKeys.lists(userId),
    createFn: apiCreateReportDocument,
    updateFn: apiUpdateReportDocument,
    deleteFn: apiDeleteReportDocument,
    onCreated: (saved, document) =>
      queryClient.setQueryData(
        reportDocumentQueryKeys.detail(document.id),
        saved
      ),
    onUpdated: (saved, { id }) =>
      queryClient.setQueryData(reportDocumentQueryKeys.detail(id), saved),
    onDeleted: (id) =>
      queryClient.removeQueries({
        queryKey: reportDocumentQueryKeys.detail(id),
      }),
  });

  return {
    createReportDocument: create,
    updateReportDocument: update,
    deleteReportDocument: remove,
  };
}
